import { ComboBox } from '@domgen/base-design-system-2.0/atoms/ComboBox';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { useQuery } from '@domgen/base-design-system-2.0/hooks/useQuery';
import { ModelNumberLookupResponse } from '../models/services/api/queryModelNumber';
import { queryModelNumber } from '../services/api/queryModelNumber';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { useEffect, useState } from 'react';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { ArrowLeft } from '../assets/icons/ArrowLeft';
import {
  formApplianceName,
  formCategoryName,
  store,
  useApplianceFormData,
  useApplianceLookupResults,
  useAppState,
  useUrlParams,
} from '../store/store';
import { StepKey } from '../enums/stepKey';
import { useFormValidation } from '../hooks/useFormValidation';
import { formValidators } from '../utils/form/validators';
import { StoreEvent } from '../store/store.types';
import { CircleCheck } from '../assets/icons/CircleCheck';
import { gaEvent, gaPageView } from '../services/ga/ga';
import { usePostHog } from '@posthog/react';
import { FormKey } from '../enums/formKey';
import { getSystemCode } from '../utils/getSystemCode';
import { capturePostHogEvent } from '../services/posthog/capture';

type ModelNumberPageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function ModelNumberPage({ stepNumber, totalSteps }: ModelNumberPageProps) {
  const posthog = usePostHog();
  const [waitForSearchError, setWaitForSearchError] = useState(false);

  const session = useAppState().session;
  const applianceModelResults = useApplianceLookupResults();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);

  const { setQuery, results, loading } = useQuery<ModelNumberLookupResponse>(queryModelNumber, {
    minQueryLength: 3,
  });

  useEffect(() => {
    if (!results?.appliances) {
      return;
    }
    store.send({
      type: StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS,
      data: results.appliances,
    });
  }, [results]);

  const [displayHelpText, setDisplayHelpText] = useState(false);
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  const { formValues, errors, handleChange, validateAll, setFormValues } = useFormValidation(
    {
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    },
    {
      modelNumber: [
        formValidators.required('Model number is required'),
        formValidators.alphanumeric('Please enter a valid model number'),
        formValidators.maxLength(30, 'Model number is too long'),
      ],
      serialNumber: [
        formValidators.required('Serial number is required'),
        formValidators.minLength(9, 'Serial number must be at least 9 characters'),
        formValidators.maxLength(20, 'Serial number must be at most 20 characters'),
        formValidators.alphanumeric('Please enter a valid serial number'),
      ],
    },
  );

  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName: applianceName,
      applianceCategory: applianceCategory,
    });
  }, []);

  useEffect(() => {
    setFormValues({
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    });
  }, [formData, setFormValues]);

  const handleHelpTextClick = () => {
    setDisplayHelpText(!displayHelpText);
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'No Model/Serial Number',
      eventLabel: 'No Model/Serial CTA Clicked',
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
    });
    capturePostHogEvent(posthog, 'no-model-serial-cta-clicked', store.get());
  };

  const handleContinue = () => {
    if (loading) {
      setWaitForSearchError(true);
      return;
    }
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !formValues.modelNumber || !formValues.serialNumber ? 'failed' : 'success',
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
    });
    capturePostHogEvent(posthog, 'model-serial-continue-cta-clicked', store.get());
    const isValid = validateAll();

    if (!isValid) return;

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'serialNumber',
      eventLabel: formValues.serialNumber,
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
    });
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'modelNumber',
      eventLabel: formValues.modelNumber,
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
    });

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.MODEL_SERIAL]: {
          [FormKey.MODEL_NUMBER]: formValues.modelNumber,
          [FormKey.SERIAL_NUMBER]: formValues.serialNumber,
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: !formValues.modelNumber || !formValues.serialNumber ? 'failed' : 'success',
      pagename: 'model-serial',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode ?? 'DandG_IPR',
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id ?? '',
    });

    capturePostHogEvent(posthog, 'model-serial-back-clicked', store.get());
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.MODEL_SERIAL } });
  };

  const handleContactClick = () => {
    capturePostHogEvent(posthog, 'model-serial-contact-clicked', store.get());
  };

  const modelSelectOptions = (results?.appliances || applianceModelResults)?.map((appliance) => ({
    label: appliance.modelNumber,
    value: appliance.modelNumber,
    description: appliance.applianceName,
  }));

  const handleModelSelect = (value: string | null) => {
    setWaitForSearchError(false);

    handleChange({ target: { name: 'modelNumber', value: value ?? '' } });
    capturePostHogEvent(posthog, 'model-entered', store.get());
  };

  const handleModelInputChange = (value: string) => {
    setWaitForSearchError(false);
    setQuery(value);
    handleChange({ target: { name: 'modelNumber', value } });
  };

  const generateOptions = () => {
    if (
      modelSelectOptions &&
      modelSelectOptions.some((option) => option.value === formValues.modelNumber)
    ) {
      return modelSelectOptions;
    }
    if (formValues.modelNumber.length < 3 || errors.modelNumber) {
      return [];
    }
    return [
      ...(modelSelectOptions || []),
      { label: formValues.modelNumber, value: formValues.modelNumber },
    ];
  };

  const handleSerialInputBlur = () => {
    if (formValues.serialNumber && !errors.serialNumber) {
      capturePostHogEvent(posthog, 'serial-entered', store.get());
    }
  };

  return (
    <div className="flex justify-center items-center bg-bg-main py-space-700 px-space-500">
      <div className="w-2xl h-auto p-5 flex flex-col justify-center rounded-radius-100 bg-bg-main-container">
        <ProgressBars total={totalSteps} current={stepNumber} />
        <h1 className="my-space-400 text-h3">Please provide your model and serial number</h1>
        <p className="mb-space-400">
          Please enter your model and serial number below, this will help us provide you with a
          better warranty service if you ever need a repair.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row">
          <ComboBox
            label="Model number"
            name="modelNumber"
            placeholder="Search for a model number"
            onInputChange={handleModelInputChange}
            options={generateOptions()}
            defaultValue={formValues.modelNumber}
            asyncProps={{ loading }}
            noResultsText="No appliances found"
            hintText="For example 1234567ABC"
            required
            onSelect={handleModelSelect}
            hasSuccess={!!formValues.modelNumber && !errors['modelNumber']}
            hasError={
              waitForSearchError && loading
                ? 'Please wait for the search to finish before continuing'
                : errors['modelNumber']
            }
            allowCustomValue
          />
          <Input
            hintText="For example 1234567ABC"
            placeholder="Enter a serial number"
            value={formValues.serialNumber}
            label="Serial number"
            name="serialNumber"
            onChange={handleChange}
            onBlur={handleSerialInputBlur}
            hasSuccess={!!formValues.serialNumber && !errors['serialNumber']}
            hasError={errors['serialNumber']}
            rightIcon={
              formValues.serialNumber && !errors.serialNumber ? <CircleCheck /> : undefined
            }
          />
        </div>
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            className="cursor-pointer text-body-link-strong"
            onClick={handleHelpTextClick}
          >
            I don’t have my model or serial number
          </button>
        </div>

        {
          <div
            id="help-text"
            className={`
              rounded-md
              transition-all duration-500 overflow-hidden
              ${displayHelpText ? 'max-h-40 opacity-100 p-4 mt-4' : 'max-h-0 opacity-0'}
              bg-(--bg-main-container-color) border border-(--color-border-neutral)
            `}
          >
            <p>
              If your model or serial numbers aren’t recognized or you don’t have them to hand, call
              us at{' '}
              <a
                onClick={handleContactClick}
                className="text-body-link-strong"
                href="tel:+18335522314"
              >
                +1 833-552-2314
              </a>{' '}
              for support.
            </p>
          </div>
        }

        <div className="flex flex-col-reverse sm:flex-row gap-4 sm:justify-between items-center w-full mt-5">
          <div>
            <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
              Go back
            </TextLink>
          </div>
          <div className="w-full sm:w-fit">
            <Button onClick={handleContinue}>Continue</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
